/**
 * Troubleshooting Tracker Service
 *
 * Centralized service for tracking UI activities for troubleshooting purposes.
 * Designed to be extensible and performance-efficient.
 */

import { Logger } from '@saviynt/common';
import { v4 as uuidv4 } from 'uuid';

import type { Logger as ILogger } from './loggers/Logger';

import ConsoleLogger from './loggers/ConsoleLogger';
import { isTroubleshootingEnabled } from './storageUtils';
import {
  DataProviderTriggerType,
  DataProviderType,
  type DependencyMap,
  type DependencyRelationship,
  type FormStructureForDependencyMap,
  type HierarchyTreeNode,
  type LevelWiseHierarchy,
  LogLevel,
  type Statistics,
  type TrackingEvent,
  TrackingEventType,
  TrackingFeature,
  type TrackingFilter,
  type TrackingOptions,
} from './types';

const DEFAULT_MAX_EVENTS = 200; // Increased from 50 for better debugging coverage
const DEFAULT_ENABLED = isTroubleshootingEnabled(); // Check localStorage
const DEFAULT_LOG_LEVEL = LogLevel.INFO;

class TroubleshootingTracker {
  private events: TrackingEvent[] = [];

  private maxEvents: number;

  private enabled: boolean;

  private logLevel: LogLevel;

  private loggers: Map<string, ILogger> = new Map();

  constructor(options: TrackingOptions = {}) {
    this.maxEvents = options.maxEvents ?? DEFAULT_MAX_EVENTS;
    this.enabled = options.enabled ?? DEFAULT_ENABLED;
    this.logLevel = options.logLevel ?? DEFAULT_LOG_LEVEL;

    // Add default console logger
    const consoleLogger = new ConsoleLogger(this.logLevel);

    this.addLogger(consoleLogger);
  }

  /**
   * Enable or disable tracking
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if tracking is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Set minimum log level
   */
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.loggers.forEach((logger) => {
      if (logger instanceof ConsoleLogger) {
        logger.setLogLevel(level);
      }
    });
  }

  /**
   * Get current log level
   */
  getLogLevel(): LogLevel {
    return this.logLevel;
  }

  /**
   * Track an event
   */
  track(
    feature: TrackingFeature,
    type: TrackingEventType,
    data: Record<string, unknown>,
    duration?: number,
    metadata?: Record<string, unknown>,
    level: LogLevel = LogLevel.INFO,
    correlationId?: string,
    parentEventId?: string
  ): string {
    if (!this.enabled) {
      return '';
    }

    try {
      // Extract triggerType and dataProviderType from data if present (for DATA_PROVIDER events)
      const triggerType =
        feature === TrackingFeature.DATA_PROVIDER && 'triggerType' in data
          ? (data.triggerType as DataProviderTriggerType)
          : undefined;
      const dataProviderType =
        feature === TrackingFeature.DATA_PROVIDER && 'dataProviderType' in data
          ? (data.dataProviderType as DataProviderType)
          : undefined;

      const event: TrackingEvent = {
        id: uuidv4(),
        timestamp: Date.now(),
        feature,
        type,
        data,
        duration,
        metadata,
        level,
        correlationId,
        parentEventId,
        triggerType,
        dataProviderType,
      };

      this.events.push(event);

      // Maintain max events limit using ring buffer approach
      if (this.events.length > this.maxEvents) {
        this.events.shift(); // Remove oldest event
      }

      // Log to all registered loggers
      this.loggers.forEach((logger) => {
        try {
          logger.log(event);
        } catch (error) {
          // Silently fail - logging should never break the app
          Logger.warn('Logger error:', error);
        }
      });

      return event.id;
    } catch (error) {
      // Silently fail - tracking should never break the app
      Logger.warn('Tracking error:', error);

      return '';
    }
  }

  /**
   * Get all events matching the filter
   */
  getEvents(filter?: TrackingFilter): TrackingEvent[] {
    if (!filter) {
      return [...this.events];
    }

    return this.events.filter((event) => {
      if (filter.feature && event.feature !== filter.feature) {
        return false;
      }

      if (filter.type && event.type !== filter.type) {
        return false;
      }

      if (filter.startTime && event.timestamp < filter.startTime) {
        return false;
      }

      if (filter.endTime && event.timestamp > filter.endTime) {
        return false;
      }

      if (filter.fieldId && event.data.fieldId !== filter.fieldId) {
        return false;
      }

      return !(
        filter.level !== undefined &&
        (event.level ?? LogLevel.INFO) < filter.level
      );
    });
  }

  /**
   * Get events for a specific feature
   */
  getEventsByFeature(feature: TrackingFeature): TrackingEvent[] {
    return this.getEvents({ feature });
  }

  /**
   * Get events for cascading feature
   */
  getCascadingEvents(): TrackingEvent[] {
    return this.getEventsByFeature(TrackingFeature.CASCADING);
  }

  /**
   * Clear all events
   */
  clear(): void {
    this.events = [];
    this.loggers.forEach((logger) => {
      logger.clear?.();
    });
  }

  /**
   * Get event count
   */
  getEventCount(): number {
    return this.events.length;
  }

  /**
   * Get statistics about tracked events
   */
  getStatistics(): Statistics {
    const stats: Statistics = {
      totalEvents: this.events.length,
      eventsByFeature: {},
      eventsByType: {},
    };

    // Always calculate performance stats (MFE metrics don't require events)
    stats.performanceStats = this.calculatePerformanceStats();

    if (this.events.length === 0) {
      return stats;
    }

    let totalDuration = 0;
    let durationCount = 0;
    const timestamps: number[] = [];

    this.events.forEach((event) => {
      // Count by feature
      stats.eventsByFeature[event.feature] =
        (stats.eventsByFeature[event.feature] || 0) + 1;

      // Count by type
      stats.eventsByType[event.type] =
        (stats.eventsByType[event.type] || 0) + 1;

      // Calculate duration stats
      if (event.duration !== undefined) {
        totalDuration += event.duration;
        durationCount += 1;
      }

      timestamps.push(event.timestamp);
    });

    if (durationCount > 0) {
      stats.averageDuration = totalDuration / durationCount;
    }

    timestamps.sort((a, b) => a - b);
    const [oldestTimestamp] = timestamps;
    const newestTimestamp = timestamps[timestamps.length - 1];

    stats.oldestEvent = oldestTimestamp;
    stats.newestEvent = newestTimestamp;

    // Calculate data provider statistics
    const dataProviderEvents = this.events.filter(
      (e) => e.feature === TrackingFeature.DATA_PROVIDER
    );

    if (dataProviderEvents.length > 0) {
      stats.dataProviderStats =
        this.calculateDataProviderStats(dataProviderEvents);
    }

    return stats;
  }

  /**
   * Calculate performance statistics for MFE, Form API, and Render
   */
  private calculatePerformanceStats(): Statistics['performanceStats'] {
    // Get MFE metrics from Performance API
    let mfeBootstrapTime: number | undefined;
    let mfeMountTime: number | undefined;
    let mfeTotalLoadTime: number | undefined;

    try {
      if (typeof performance !== 'undefined') {
        const bootstrapMeasure = performance.getEntriesByName(
          'ff-mfe-bootstrap',
          'measure'
        )[0] as PerformanceMeasure | undefined;

        const mountMeasure = performance.getEntriesByName(
          'ff-mfe-mount',
          'measure'
        )[0] as PerformanceMeasure | undefined;

        const totalMeasure = performance.getEntriesByName(
          'ff-mfe-total',
          'measure'
        )[0] as PerformanceMeasure | undefined;

        mfeBootstrapTime = bootstrapMeasure?.duration;
        mfeMountTime = mountMeasure?.duration;
        mfeTotalLoadTime = totalMeasure?.duration;
      }
    } catch {
      // Performance API not available
    }

    // Get Form API calls from events
    const formApiEvents = this.events.filter(
      (e) => e.feature === TrackingFeature.FORM_API
    );

    const formApiCalls: Array<{
      type: 'GET' | 'DA';
      formId: string;
      duration: number;
      status: 'success' | 'error';
      timestamp: number;
    }> = formApiEvents.map((event) => ({
      type: (event.data.type as 'GET' | 'DA') || 'GET',
      formId: (event.data.formId as string) || '',
      duration: event.duration || (event.data.duration as number) || 0,
      status: (event.data.status as 'success' | 'error') || 'success',
      timestamp: event.timestamp,
    }));

    // Get Form Render calls from events
    const formRenderEvents = this.events.filter(
      (e) => e.feature === TrackingFeature.FORM_RENDER
    );

    const formRenderCalls: Array<{
      fieldCount: number;
      duration: number;
      urlSlug?: string;
      timestamp: number;
    }> = formRenderEvents.map((event) => ({
      fieldCount: (event.data.fieldCount as number) || 0,
      duration: event.duration || (event.data.duration as number) || 0,
      urlSlug: event.data.urlSlug as string | undefined,
      timestamp: event.timestamp,
    }));

    // Calculate init load stats from DATA_PROVIDER events
    const initLoadStats = this.calculateInitLoadStats();

    return {
      mfeBootstrapTime,
      mfeMountTime,
      mfeTotalLoadTime,
      formApiCalls,
      formRenderCalls,
      initLoadTime: initLoadStats?.initLoadTime,
      initLoadApiCount: initLoadStats?.initLoadApiCount,
      initLoadSuccessCount: initLoadStats?.successCount,
      initLoadErrorCount: initLoadStats?.errorCount,
    };
  }

  /**
   * Calculate init load statistics from all API events
   * Includes both FORM_API (form/get, form/da) and DATA_PROVIDER events
   */
  private calculateInitLoadStats(): {
    initLoadTime: number;
    initLoadApiCount: number;
    successCount: number;
    errorCount: number;
  } | null {
    // Use Performance API for accurate page load time
    let pageLoadTime: number;

    try {
      pageLoadTime =
        typeof performance !== 'undefined'
          ? performance.timeOrigin
          : this.events[0]?.timestamp || Date.now();
    } catch {
      pageLoadTime = this.events[0]?.timestamp || Date.now();
    }

    // Consider API calls within first 15 seconds as "initial load"
    const INIT_WINDOW_MS = 15000;

    // Collect all initial load API events
    const allInitialApiEvents: TrackingEvent[] = [];

    // 1. Find FORM_API events (form/get, form/da calls) within init window
    // These don't have TRIGGERED events, just SUCCESS/ERROR with duration
    const formApiEvents = this.events.filter(
      (e) =>
        e.feature === TrackingFeature.FORM_API &&
        (e.type === TrackingEventType.FORM_API_GET_SUCCESS ||
          e.type === TrackingEventType.FORM_API_GET_ERROR ||
          e.type === TrackingEventType.FORM_API_DA_SUCCESS ||
          e.type === TrackingEventType.FORM_API_DA_ERROR) &&
        e.timestamp - pageLoadTime < INIT_WINDOW_MS
    );

    allInitialApiEvents.push(...formApiEvents);

    // 2. Find DATA_PROVIDER triggers within the init window
    const dataProviderTriggers = this.events.filter(
      (e) =>
        e.feature === TrackingFeature.DATA_PROVIDER &&
        e.type === TrackingEventType.DATA_PROVIDER_API_TRIGGERED &&
        e.timestamp - pageLoadTime < INIT_WINDOW_MS
    );

    // Find completion events for DATA_PROVIDER triggers
    const triggerIds = new Set(dataProviderTriggers.map((e) => e.id));
    const dataProviderCompletions = this.events.filter(
      (e) =>
        e.correlationId &&
        triggerIds.has(e.correlationId) &&
        (e.type === TrackingEventType.DATA_PROVIDER_API_SUCCESS ||
          e.type === TrackingEventType.DATA_PROVIDER_API_ERROR)
    );

    allInitialApiEvents.push(...dataProviderCompletions);

    // If no initial API events found, return null
    if (allInitialApiEvents.length === 0) {
      return null;
    }

    // Calculate timing
    // For FORM_API events, the start time is timestamp - duration
    // For DATA_PROVIDER events, use the trigger timestamp
    let firstApiStart = Infinity;
    let lastApiEnd = 0;

    // Process FORM_API events (they have duration in data)
    formApiEvents.forEach((e) => {
      const duration = e.duration || (e.data.duration as number) || 0;
      const startTime = e.timestamp - duration;

      firstApiStart = Math.min(firstApiStart, startTime);
      lastApiEnd = Math.max(lastApiEnd, e.timestamp);
    });

    // Process DATA_PROVIDER triggers and completions
    dataProviderTriggers.forEach((e) => {
      firstApiStart = Math.min(firstApiStart, e.timestamp);
    });

    dataProviderCompletions.forEach((e) => {
      lastApiEnd = Math.max(lastApiEnd, e.timestamp);
    });

    // Count successes and errors
    const successCount =
      formApiEvents.filter(
        (e) =>
          e.type === TrackingEventType.FORM_API_GET_SUCCESS ||
          e.type === TrackingEventType.FORM_API_DA_SUCCESS
      ).length +
      dataProviderCompletions.filter(
        (e) => e.type === TrackingEventType.DATA_PROVIDER_API_SUCCESS
      ).length;

    const errorCount =
      formApiEvents.filter(
        (e) =>
          e.type === TrackingEventType.FORM_API_GET_ERROR ||
          e.type === TrackingEventType.FORM_API_DA_ERROR
      ).length +
      dataProviderCompletions.filter(
        (e) => e.type === TrackingEventType.DATA_PROVIDER_API_ERROR
      ).length;

    const totalApiCount = formApiEvents.length + dataProviderTriggers.length;

    return {
      initLoadTime: lastApiEnd - firstApiStart,
      initLoadApiCount: totalApiCount,
      successCount,
      errorCount,
    };
  }

  /**
   * Calculate detailed statistics for data provider events
   * Optimized: Single-pass aggregation instead of multiple iterations
   */
  // eslint-disable-next-line class-methods-use-this
  private calculateDataProviderStats(
    events: TrackingEvent[]
  ): Statistics['dataProviderStats'] {
    // Filter API events once
    const apiEvents = events.filter(
      (e) =>
        e.type === TrackingEventType.DATA_PROVIDER_API_TRIGGERED ||
        e.type === TrackingEventType.DATA_PROVIDER_API_SUCCESS ||
        e.type === TrackingEventType.DATA_PROVIDER_API_ERROR
    );

    if (apiEvents.length === 0) {
      return {
        totalApiCalls: 0,
        successCount: 0,
        errorCount: 0,
        successRate: 0,
        errorRate: 0,
        averageResponseTime: 0,
        medianResponseTime: 0,
        minResponseTime: 0,
        maxResponseTime: 0,
        slowestCalls: [],
        callsByElementId: {},
        callsByTriggerType: {
          [DataProviderTriggerType.INITIAL_LOAD]: {
            total: 0,
            success: 0,
            error: 0,
            errorRate: 0,
          },
          [DataProviderTriggerType.PARENT_CHANGE]: {
            total: 0,
            success: 0,
            error: 0,
            errorRate: 0,
          },
          [DataProviderTriggerType.PAGINATION]: {
            total: 0,
            success: 0,
            error: 0,
            errorRate: 0,
          },
        },
        callsByProviderType: {
          [DataProviderType.HTTP]: {
            total: 0,
            success: 0,
            error: 0,
            errorRate: 0,
          },
          [DataProviderType.SQL]: {
            total: 0,
            success: 0,
            error: 0,
            errorRate: 0,
          },
        },
        callsByStatusCode: {},
        averageResponseDataCount: 0,
        totalItemsLoaded: 0,
        averagePageNumber: 0,
        errorsByType: {},
        errorsByStatusCode: {},
      };
    }

    // Initialize aggregators for single-pass calculation
    let triggeredCount = 0;
    let successCount = 0;
    let errorCount = 0;
    const responseTimes: number[] = [];
    const slowestCallsData: Array<{
      fieldId: string;
      fieldName: string;
      duration: number;
      triggerType: DataProviderTriggerType;
      timestamp: number;
      statusCode?: number;
    }> = [];

    const callsByElementId: Record<
      string,
      { total: number; success: number; error: number }
    > = {};

    const callsByTriggerType: Record<
      DataProviderTriggerType,
      { total: number; success: number; error: number }
    > = {
      [DataProviderTriggerType.INITIAL_LOAD]: {
        total: 0,
        success: 0,
        error: 0,
      },
      [DataProviderTriggerType.PARENT_CHANGE]: {
        total: 0,
        success: 0,
        error: 0,
      },
      [DataProviderTriggerType.PAGINATION]: {
        total: 0,
        success: 0,
        error: 0,
      },
    };

    const callsByProviderType: Record<
      DataProviderType,
      { total: number; success: number; error: number }
    > = {
      [DataProviderType.HTTP]: {
        total: 0,
        success: 0,
        error: 0,
      },
      [DataProviderType.SQL]: {
        total: 0,
        success: 0,
        error: 0,
      },
    };

    const callsByStatusCode: Record<number, number> = {};
    const dataCounts: number[] = [];
    let totalItemsLoaded = 0;
    const pageNumbers: number[] = [];
    const errorsByType: Record<string, number> = {};
    const errorsByStatusCode: Record<number, number> = {};

    // Single-pass aggregation
    apiEvents.forEach((event) => {
      const data = event.data as {
        fieldId: string;
        fieldName: string;
        duration?: number;
        startTime?: number;
        endTime?: number;
        page?: number;
        apiResponse?: {
          statusCode?: number;
          dataCount?: number;
        };
        error?: {
          type: string;
          statusCode?: number;
        };
      };

      // Count by event type
      if (event.type === TrackingEventType.DATA_PROVIDER_API_TRIGGERED) {
        triggeredCount += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_SUCCESS) {
        successCount += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_ERROR) {
        errorCount += 1;
      }

      // Calculate duration
      const duration =
        data.duration ??
        (data.startTime && data.endTime ? data.endTime - data.startTime : 0);

      if (duration > 0) {
        responseTimes.push(duration);

        // Track slowest calls
        slowestCallsData.push({
          fieldId: data.fieldId || '',
          fieldName: data.fieldName || '',
          duration,
          triggerType:
            event.triggerType || DataProviderTriggerType.INITIAL_LOAD,
          timestamp: event.timestamp,
          statusCode: data.apiResponse?.statusCode,
        });
      }

      // Breakdown by element ID
      const fieldId = data.fieldId || 'unknown';

      if (!callsByElementId[fieldId]) {
        callsByElementId[fieldId] = { total: 0, success: 0, error: 0 };
      }

      // Only count triggered events for total to avoid double-counting
      if (event.type === TrackingEventType.DATA_PROVIDER_API_TRIGGERED) {
        callsByElementId[fieldId].total += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_SUCCESS) {
        callsByElementId[fieldId].success += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_ERROR) {
        callsByElementId[fieldId].error += 1;
      }

      // Breakdown by trigger type
      const triggerType =
        event.triggerType || DataProviderTriggerType.INITIAL_LOAD;

      if (event.type === TrackingEventType.DATA_PROVIDER_API_TRIGGERED) {
        callsByTriggerType[triggerType].total += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_SUCCESS) {
        callsByTriggerType[triggerType].success += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_ERROR) {
        callsByTriggerType[triggerType].error += 1;
      }

      // Breakdown by provider type
      const providerType = event.dataProviderType || DataProviderType.HTTP;

      if (event.type === TrackingEventType.DATA_PROVIDER_API_TRIGGERED) {
        callsByProviderType[providerType].total += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_SUCCESS) {
        callsByProviderType[providerType].success += 1;
      } else if (event.type === TrackingEventType.DATA_PROVIDER_API_ERROR) {
        callsByProviderType[providerType].error += 1;
      }

      // Breakdown by status code
      const statusCode = data.apiResponse?.statusCode ?? data.error?.statusCode;

      if (statusCode !== undefined) {
        callsByStatusCode[statusCode] =
          (callsByStatusCode[statusCode] || 0) + 1;
      }

      // Response metrics
      if (data.apiResponse?.dataCount !== undefined) {
        dataCounts.push(data.apiResponse.dataCount);
        totalItemsLoaded += data.apiResponse.dataCount;
      }

      // Page numbers
      if (data.page !== undefined) {
        pageNumbers.push(data.page);
      }

      // Error breakdown (only for error events)
      if (event.type === TrackingEventType.DATA_PROVIDER_API_ERROR) {
        if (data.error?.type) {
          errorsByType[data.error.type] =
            (errorsByType[data.error.type] || 0) + 1;
        }

        if (data.error?.statusCode) {
          errorsByStatusCode[data.error.statusCode] =
            (errorsByStatusCode[data.error.statusCode] || 0) + 1;
        }
      }
    });

    // Calculate derived metrics
    const totalApiCalls = triggeredCount;
    const successRate =
      totalApiCalls > 0 ? (successCount / totalApiCalls) * 100 : 0;
    const errorRate =
      totalApiCalls > 0 ? (errorCount / totalApiCalls) * 100 : 0;

    const averageResponseTime =
      responseTimes.length > 0
        ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
        : 0;

    // Calculate median, min, max
    const sortedResponseTimes = [...responseTimes].sort((a, b) => a - b);
    let medianResponseTime = 0;

    if (sortedResponseTimes.length > 0) {
      if (sortedResponseTimes.length % 2 === 0) {
        medianResponseTime =
          (sortedResponseTimes[sortedResponseTimes.length / 2 - 1] +
            sortedResponseTimes[sortedResponseTimes.length / 2]) /
          2;
      } else {
        medianResponseTime =
          sortedResponseTimes[Math.floor(sortedResponseTimes.length / 2)];
      }
    }

    const minResponseTime =
      sortedResponseTimes.length > 0 ? sortedResponseTimes[0] : 0;
    const maxResponseTime =
      sortedResponseTimes.length > 0
        ? sortedResponseTimes[sortedResponseTimes.length - 1]
        : 0;

    // Get slowest calls
    const slowestCalls = slowestCallsData
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5);

    // Calculate error rates for trigger types
    const callsByTriggerTypeWithRates: Record<
      DataProviderTriggerType,
      { total: number; success: number; error: number; errorRate: number }
    > = {
      [DataProviderTriggerType.INITIAL_LOAD]: {
        ...callsByTriggerType[DataProviderTriggerType.INITIAL_LOAD],
        errorRate:
          callsByTriggerType[DataProviderTriggerType.INITIAL_LOAD].total > 0
            ? (callsByTriggerType[DataProviderTriggerType.INITIAL_LOAD].error /
                callsByTriggerType[DataProviderTriggerType.INITIAL_LOAD]
                  .total) *
              100
            : 0,
      },
      [DataProviderTriggerType.PARENT_CHANGE]: {
        ...callsByTriggerType[DataProviderTriggerType.PARENT_CHANGE],
        errorRate:
          callsByTriggerType[DataProviderTriggerType.PARENT_CHANGE].total > 0
            ? (callsByTriggerType[DataProviderTriggerType.PARENT_CHANGE].error /
                callsByTriggerType[DataProviderTriggerType.PARENT_CHANGE]
                  .total) *
              100
            : 0,
      },
      [DataProviderTriggerType.PAGINATION]: {
        ...callsByTriggerType[DataProviderTriggerType.PAGINATION],
        errorRate:
          callsByTriggerType[DataProviderTriggerType.PAGINATION].total > 0
            ? (callsByTriggerType[DataProviderTriggerType.PAGINATION].error /
                callsByTriggerType[DataProviderTriggerType.PAGINATION].total) *
              100
            : 0,
      },
    };

    // Calculate error rates for provider types
    const callsByProviderTypeWithRates: Record<
      DataProviderType,
      { total: number; success: number; error: number; errorRate: number }
    > = {
      [DataProviderType.HTTP]: {
        ...callsByProviderType[DataProviderType.HTTP],
        errorRate:
          callsByProviderType[DataProviderType.HTTP].total > 0
            ? (callsByProviderType[DataProviderType.HTTP].error /
                callsByProviderType[DataProviderType.HTTP].total) *
              100
            : 0,
      },
      [DataProviderType.SQL]: {
        ...callsByProviderType[DataProviderType.SQL],
        errorRate:
          callsByProviderType[DataProviderType.SQL].total > 0
            ? (callsByProviderType[DataProviderType.SQL].error /
                callsByProviderType[DataProviderType.SQL].total) *
              100
            : 0,
      },
    };

    const averageResponseDataCount =
      dataCounts.length > 0
        ? dataCounts.reduce((a, b) => a + b, 0) / dataCounts.length
        : 0;

    const averagePageNumber =
      pageNumbers.length > 0
        ? pageNumbers.reduce((a, b) => a + b, 0) / pageNumbers.length
        : 0;

    return {
      totalApiCalls,
      successCount,
      errorCount,
      successRate,
      errorRate,
      averageResponseTime,
      medianResponseTime,
      minResponseTime,
      maxResponseTime,
      slowestCalls,
      callsByElementId,
      callsByTriggerType: callsByTriggerTypeWithRates,
      callsByProviderType: callsByProviderTypeWithRates,
      callsByStatusCode,
      averageResponseDataCount,
      totalItemsLoaded,
      averagePageNumber,
      errorsByType,
      errorsByStatusCode,
    };
  }

  /**
   * Logger management
   */
  addLogger(logger: ILogger): void {
    this.loggers.set(logger.id, logger);
  }

  removeLogger(loggerId: string): void {
    const logger = this.loggers.get(loggerId);

    if (logger) {
      logger.destroy?.();
      this.loggers.delete(loggerId);
    }
  }

  getLoggers(): ILogger[] {
    return Array.from(this.loggers.values());
  }

  /**
   * Set max events
   */
  setMaxEvents(max: number): void {
    this.maxEvents = max;

    // Trim events if over limit
    if (this.events.length > max) {
      this.events = this.events.slice(-max);
    }
  }

  /**
   * Build dependency map from form structure and/or tracked events
   * Creates a bidirectional map showing which fields depend on which other fields
   * @param formStructure - Optional form structure to build map from (canvasFields + dataProviders)
   */
  buildDependencyMap(
    formStructure?: FormStructureForDependencyMap
  ): DependencyMap {
    const dependencies: Record<string, Set<string>> = {};
    const dependents: Record<string, Set<string>> = {};
    const fieldNames: Record<string, string> = {};

    // Helper to register a dependency relationship
    const addDependency = (
      fieldId: string,
      depFieldId: string,
      depFieldName: string
    ): void => {
      if (!dependencies[fieldId]) {
        dependencies[fieldId] = new Set<string>();
      }

      dependencies[fieldId].add(depFieldId);
      fieldNames[depFieldId] = depFieldName;

      if (!dependents[depFieldId]) {
        dependents[depFieldId] = new Set<string>();
      }

      dependents[depFieldId].add(fieldId);
    };

    // Helper to extract field names referenced in an expression string
    const extractExpressionFieldNames = (
      expression: string,
      allFieldNames: string[]
    ): string[] => {
      const referenced: string[] = [];

      allFieldNames.forEach((name) => {
        if (!name) {
          return;
        }

        // Use word boundary matching to avoid false positives
        // e.g. "username" should not match field "name"
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(`\\b${escaped}\\b`);

        if (pattern.test(expression)) {
          referenced.push(name);
        }
      });

      return referenced;
    };

    // Build from form structure if provided (primary source)
    if (formStructure) {
      const { canvasFields, dataProviders } = formStructure;

      // Collect all field names for expression matching
      const allFieldNamesList = canvasFields
        .map((f) => f.fieldName)
        .filter((name): name is string => !!name);

      // Register ALL fields first
      canvasFields.forEach((field) => {
        const fieldId: string = field.id;

        fieldNames[fieldId] = field.fieldName || field.id;

        if (!dependencies[fieldId]) {
          dependencies[fieldId] = new Set<string>();
        }
      });

      // Process data provider dependencies
      canvasFields.forEach((field) => {
        const fieldId: string = field.id;
        const dataProviderId = field.dataProviderId || field.dataProvider;

        if (dataProviderId) {
          const dataProvider = dataProviders[dataProviderId];

          if (
            dataProvider?.dependencies &&
            Object.keys(dataProvider.dependencies).length > 0
          ) {
            const processedDeps = new Set<string>();

            Object.keys(dataProvider.dependencies).forEach((depFieldName) => {
              if (processedDeps.has(depFieldName)) {
                return;
              }

              processedDeps.add(depFieldName);

              const depField = canvasFields.find(
                (f) => f.fieldName === depFieldName || f.id === depFieldName
              );

              if (depField) {
                addDependency(
                  fieldId,
                  depField.id,
                  depField.fieldName || depField.id
                );
              }
            });
          }
        }
      });

      // Process expression dependencies (visibility, required, editable)
      canvasFields.forEach((field) => {
        const fieldId: string = field.id;
        const fieldName: string = field.fieldName || field.id;

        if (field.expressions) {
          field.expressions.forEach((expr) => {
            if (!expr.enabled) {
              return;
            }

            expr.value.forEach((v) => {
              if (!v.value || !v.selected) {
                return;
              }

              const referencedNames = extractExpressionFieldNames(
                v.value,
                allFieldNamesList.filter((n) => n !== fieldName)
              );

              referencedNames.forEach((refName) => {
                const refField = canvasFields.find(
                  (f) => f.fieldName === refName
                );

                if (refField) {
                  addDependency(
                    fieldId,
                    refField.id,
                    refField.fieldName || refField.id
                  );
                }
              });
            });
          });
        }

        // Process default value expressions
        if (field.defaultValue?.isExpression && field.defaultValue.value) {
          const referencedNames = extractExpressionFieldNames(
            field.defaultValue.value,
            allFieldNamesList.filter((n) => n !== fieldName)
          );

          referencedNames.forEach((refName) => {
            const refField = canvasFields.find((f) => f.fieldName === refName);

            if (refField) {
              addDependency(
                fieldId,
                refField.id,
                refField.fieldName || refField.id
              );
            }
          });
        }
      });
    }

    // Also build from tracked events (supplement with runtime data)
    // Only if no form structure was provided (events use fieldNames, not UUIDs)
    if (!formStructure) {
      const apiEvents = this.events.filter(
        (e) =>
          e.feature === TrackingFeature.DATA_PROVIDER &&
          e.type === TrackingEventType.DATA_PROVIDER_API_TRIGGERED
      );

      apiEvents.forEach((event) => {
        const data = event.data as {
          fieldId: string;
          fieldName: string;
          parentFields?: Array<{
            fieldId: string;
            fieldName: string;
            selectedValue: unknown;
          }>;
        };

        const { fieldId } = data;
        const fieldName = data.fieldName || fieldId;

        // Store field name (don't override if already set from form structure)
        if (!fieldNames[fieldId]) {
          fieldNames[fieldId] = fieldName;
        }

        // Initialize sets if needed
        if (!dependencies[fieldId]) {
          dependencies[fieldId] = new Set();
        }

        // Process parent fields (dependencies)
        if (data.parentFields && data.parentFields.length > 0) {
          data.parentFields.forEach((parent) => {
            const parentId = parent.fieldId;
            const parentName = parent.fieldName || parentId;

            // Add parent to this field's dependencies
            dependencies[fieldId].add(parentId);

            // Initialize dependents set for parent if needed
            if (!dependents[parentId]) {
              dependents[parentId] = new Set();
            }

            // Add this field as a dependent of the parent
            dependents[parentId].add(fieldId);

            // Store parent field name (don't override if already set)
            if (!fieldNames[parentId]) {
              fieldNames[parentId] = parentName;
            }
          });
        }
      });
    }

    return {
      dependencies,
      dependents,
      fieldNames,
    };
  }

  /**
   * Get dependency relationship for a specific field
   * @param fieldId - The field ID to get dependencies for
   * @param formStructure - Optional form structure to build map from (uses stored structure if not provided)
   */
  getFieldDependencies(
    fieldId: string,
    formStructure?: FormStructureForDependencyMap
  ): DependencyRelationship | null {
    const structure = formStructure ?? this.formStructure ?? undefined;
    const map = this.buildDependencyMap(structure);

    if (!map.dependencies[fieldId] && !map.dependents[fieldId]) {
      return null;
    }

    const dependsOn = Array.from(map.dependencies[fieldId] || []).map(
      (depId) => ({
        fieldId: depId,
        fieldName: map.fieldNames[depId] || depId,
      })
    );

    const dependentsList = Array.from(map.dependents[fieldId] || []).map(
      (depId) => ({
        fieldId: depId,
        fieldName: map.fieldNames[depId] || depId,
      })
    );

    return {
      fieldId,
      fieldName: map.fieldNames[fieldId] || fieldId,
      dependsOn,
      dependents: dependentsList,
    };
  }

  /**
   * Get all fields that have dependencies (fields that depend on other fields)
   * @param formStructure - Optional form structure to build map from (uses stored structure if not provided)
   */
  getFieldsWithDependencies(
    formStructure?: FormStructureForDependencyMap
  ): string[] {
    const structure = formStructure ?? this.formStructure ?? undefined;
    const map = this.buildDependencyMap(structure);

    return Object.keys(map.dependencies).filter(
      (fieldId) => map.dependencies[fieldId].size > 0
    );
  }

  /**
   * Get all fields that are dependencies (fields that other fields depend on)
   * @param formStructure - Optional form structure to build map from (uses stored structure if not provided)
   */
  getDependencyFields(formStructure?: FormStructureForDependencyMap): string[] {
    const structure = formStructure ?? this.formStructure ?? undefined;
    const map = this.buildDependencyMap(structure);

    return Object.keys(map.dependents).filter(
      (fieldId) => map.dependents[fieldId].size > 0
    );
  }

  /**
   * Detect circular dependencies in the dependency map
   * Returns array of cycles found (each cycle is an array of fieldIds)
   * @param formStructure - Optional form structure to build map from (uses stored structure if not provided)
   */
  detectCircularDependencies(
    formStructure?: FormStructureForDependencyMap
  ): string[][] {
    const structure = formStructure ?? this.formStructure ?? undefined;
    const map = this.buildDependencyMap(structure);
    const cycles: string[][] = [];
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const dfs = (fieldId: string, path: string[]): void => {
      visited.add(fieldId);
      recursionStack.add(fieldId);
      path.push(fieldId);

      const dependencies = map.dependencies[fieldId];

      if (dependencies) {
        dependencies.forEach((depId) => {
          if (!visited.has(depId)) {
            dfs(depId, [...path]);
          } else if (recursionStack.has(depId)) {
            // Found a cycle
            const cycleStart = path.indexOf(depId);

            if (cycleStart !== -1) {
              const cycle = path.slice(cycleStart);

              cycle.push(depId); // Complete the cycle

              // Check if this cycle is already found (avoid duplicates)
              const cycleKey = cycle.sort().join('->');

              if (
                !cycles.some(
                  (existingCycle) =>
                    existingCycle.sort().join('->') === cycleKey
                )
              ) {
                cycles.push(cycle);
              }
            }
          }
        });
      }

      recursionStack.delete(fieldId);
    };

    // Check all fields
    Object.keys(map.dependencies).forEach((fieldId) => {
      if (!visited.has(fieldId)) {
        dfs(fieldId, []);
      }
    });

    return cycles;
  }

  /**
   * Get the full dependency map
   * @param formStructure - Optional form structure to build map from (uses stored structure if not provided)
   */
  getDependencyMap(
    formStructure?: FormStructureForDependencyMap
  ): DependencyMap {
    const structure = formStructure ?? this.formStructure ?? undefined;

    return this.buildDependencyMap(structure);
  }

  /**
   * Set form structure for dependency map building
   * This allows the tracker to proactively build dependency maps
   */
  private formStructure: FormStructureForDependencyMap | null = null;

  /**
   * Set the form structure for building dependency maps
   */
  setFormStructure(formStructure: FormStructureForDependencyMap): void {
    this.formStructure = formStructure;
  }

  /**
   * Get stored form structure
   */
  getFormStructure(): FormStructureForDependencyMap | null {
    return this.formStructure;
  }

  /**
   * Clear the stored form structure
   */
  clearFormStructure(): void {
    this.formStructure = null;
  }

  /**
   * Build hierarchy tree from dependency map
   * Returns a single tree with a virtual root node containing all root nodes as children
   */
  buildHierarchyTree(
    formStructure?: FormStructureForDependencyMap
  ): HierarchyTreeNode | null {
    const map = this.getDependencyMap(formStructure);
    const { dependencies, dependents, fieldNames } = map;

    // Find root nodes (fields with no dependencies)
    const allFieldIds = new Set<string>([
      ...Object.keys(dependencies),
      ...Object.keys(dependents),
    ]);

    const rootNodes: string[] = [];

    allFieldIds.forEach((fieldId) => {
      const deps = dependencies[fieldId];

      if (!deps || deps.size === 0) {
        rootNodes.push(fieldId);
      }
    });

    // If no fields found, return null
    if (allFieldIds.size === 0) {
      return null;
    }

    // Calculate levels for all nodes
    const levels = this.calculateNodeLevels(map);

    // Build tree for each root node
    const rootTrees: HierarchyTreeNode[] = [];
    const visited = new Set<string>();
    const fullyProcessed = new Set<string>(); // Track nodes with children already built

    const buildTree = (
      fieldId: string,
      currentLevel: number
    ): HierarchyTreeNode | null => {
      const fieldName = fieldNames[fieldId] || fieldId;
      const deps = Array.from(dependencies[fieldId] || []);

      // If already fully processed, return a reference node (no children)
      // This handles fields with multiple parents
      if (fullyProcessed.has(fieldId)) {
        return {
          fieldId,
          fieldName,
          children: [],
          dependencies: deps,
          level: levels.get(fieldId) ?? currentLevel,
          isReference: true, // Mark as reference to avoid showing subtree again
        };
      }

      // If visited but not fully processed, skip (circular dependency protection)
      if (visited.has(fieldId)) {
        return null;
      }

      visited.add(fieldId);
      const children = Array.from(dependents[fieldId] || []);

      const node: HierarchyTreeNode = {
        fieldId,
        fieldName,
        children: [],
        dependencies: deps,
        level: levels.get(fieldId) ?? currentLevel,
      };

      // Build children (fields that depend on this field)
      children.forEach((childId) => {
        const childNode = buildTree(childId, currentLevel + 1);

        if (childNode) {
          node.children.push(childNode);
        }
      });

      // Mark as fully processed (children built)
      fullyProcessed.add(fieldId);

      return node;
    };

    rootNodes.forEach((rootId) => {
      const tree = buildTree(rootId, 0);

      if (tree) {
        rootTrees.push(tree);
      }
    });

    // Handle orphaned nodes (nodes that have dependencies but aren't in any tree)
    allFieldIds.forEach((fieldId) => {
      if (!visited.has(fieldId)) {
        const fieldName = fieldNames[fieldId] || fieldId;

        const deps = Array.from(dependencies[fieldId] || []);

        rootTrees.push({
          fieldId,
          fieldName,
          children: [],
          dependencies: deps,
          level: levels.get(fieldId) ?? 0,
        });
      }
    });

    // If no root trees found, return null
    if (rootTrees.length === 0) {
      return null;
    }

    // Always create a single tree with a virtual root node containing all nodes
    // This ensures all nodes start from one parent level
    return {
      fieldId: '__root__',
      fieldName: 'Form',
      children: rootTrees,
      dependencies: [],
      level: -1, // Virtual root is at level -1
    };
  }

  /**
   * Calculate node levels using BFS
   */
  // eslint-disable-next-line class-methods-use-this
  private calculateNodeLevels(map: DependencyMap): Map<string, number> {
    const levels = new Map<string, number>();
    const { dependencies, dependents } = map;

    // Find root nodes
    const allFieldIds = new Set<string>([
      ...Object.keys(map.dependencies),
      ...Object.keys(map.dependents),
    ]);

    const rootNodes: string[] = [];

    allFieldIds.forEach((fieldId) => {
      const deps = dependencies[fieldId];

      if (!deps || deps.size === 0) {
        rootNodes.push(fieldId);
        levels.set(fieldId, 0);
      }
    });

    // BFS to assign levels
    const queue: Array<{ fieldId: string; level: number }> = rootNodes.map(
      (id) => ({ fieldId: id, level: 0 })
    );

    while (queue.length > 0) {
      const item = queue.shift();

      if (!item) {
        break;
      }

      const { fieldId, level } = item;
      const children = Array.from(dependents[fieldId] || []);

      children.forEach((childId) => {
        if (!levels.has(childId)) {
          // Level is max of parent level + 1 and any dependency levels
          const childDeps = Array.from(dependencies[childId] || []);
          const maxDepLevel = childDeps.reduce((max, depId) => {
            const depLevel = levels.get(depId);

            return depLevel !== undefined ? Math.max(max, depLevel) : max;
          }, -1);

          const childLevel = Math.max(level + 1, maxDepLevel + 1);

          levels.set(childId, childLevel);
          queue.push({ fieldId: childId, level: childLevel });
        }
      });
    }

    // Assign level 0 to any remaining nodes
    allFieldIds.forEach((fieldId) => {
      if (!levels.has(fieldId)) {
        levels.set(fieldId, 0);
      }
    });

    return levels;
  }

  /**
   * Build level-wise hierarchy
   */
  buildLevelWiseHierarchy(
    formStructure?: FormStructureForDependencyMap
  ): LevelWiseHierarchy {
    const map = this.getDependencyMap(formStructure);
    const levels = this.calculateNodeLevels(map);
    const levelWise: Record<
      number,
      Array<{
        fieldId: string;
        fieldName: string;
        dependencies: string[];
      }>
    > = {};

    let maxLevel = 0;

    levels.forEach((level, fieldId) => {
      if (!levelWise[level]) {
        levelWise[level] = [];
      }

      const deps = Array.from(map.dependencies[fieldId] || []);

      levelWise[level].push({
        fieldId,
        fieldName: map.fieldNames[fieldId] || fieldId,
        dependencies: deps,
      });

      maxLevel = Math.max(maxLevel, level);
    });

    return {
      levels: levelWise,
      maxLevel,
    };
  }
}

// Singleton instance
let trackerInstance: TroubleshootingTracker | null = null;

/**
 * Get or create the singleton tracker instance
 */
export function getTroubleshootingTracker(
  options?: TrackingOptions
): TroubleshootingTracker {
  if (!trackerInstance) {
    trackerInstance = new TroubleshootingTracker(options);
  }

  return trackerInstance;
}

/**
 * Reset the tracker instance (useful for testing)
 */
export function resetTroubleshootingTracker(): void {
  if (trackerInstance) {
    trackerInstance.clear();

    trackerInstance.getLoggers().forEach((logger) => {
      logger.destroy?.();
    });
  }

  trackerInstance = null;
}

export default TroubleshootingTracker;
